import { DaRole, daRoles, daScheduleRoleIdToKey, daScheduleRoleKeyToId } from "mocks/roleDA";
import moment from "moment";
import { CalendarData } from "types/DaHome";
import { ScheduleSelectItem, ScheduleDate, SchedulePlan, ScheduleReport } from "types/Schedule";
import { UserData } from "types/User";

export const getHelperOfUserData = (userList: UserData[], id: number | null) => {
  return userList?.find((user) => user.id === id);
};

export const returnUserRole = (user_id: number | null, userList: UserData[]) => {
  return userList.find((item) => item.id === user_id)?.role;
};

export const returnFilteredData = (date: string, daId: number | null, dataList: ScheduleReport[]) =>
  dataList.filter(
    (data: ScheduleReport) => data?.schedule_date === date && data?.assigned_to === daId,
  );

export const isScheduleSelected = (
  list: ScheduleSelectItem[],
  date?: string,
  userId?: number | null,
) => !!date && userId != null && list.some((item) => item.date === date && item.user_id === userId);

export const repeatingDispOrVisor = (
  dataList: ScheduleReport[],
  date: string,
  val: number | null | undefined,
  station: number,
) => {
  const roleLimits: Record<number, number> = {
    [daScheduleRoleKeyToId.dispatcher]: 2,
    [daScheduleRoleKeyToId.supportDispatcher]: 2,
    [daScheduleRoleKeyToId.supervisor]: 1,
    [daScheduleRoleKeyToId.fleetCoordinator]: 1,
    [daScheduleRoleKeyToId.performanceCoordinator]: 1,
    [daScheduleRoleKeyToId.leadDriver]: 1,
  };

  if (!val) return false;

  const count = dataList.filter(
    (e) => e.role === val && e.schedule_date === date && e.station === Number(station),
  ).length;

  return count >= roleLimits[val];
};

export const sumScheduleHours = (acc: number, nextValue: any, dataList: ScheduleReport[]) => {
  const roleData = daRoles[daScheduleRoleIdToKey[nextValue.role as number] as DaRole];
  if (!roleData) {
    return acc + 0; // If roleData is not found, return 0
  } else if (nextValue.role === daScheduleRoleKeyToId.helper && !nextValue.role_hours) {
    // If the role is helper and role_hours is not set, calculate based on the helper's assigned report
    const assignedToRole = dataList.find(
      (report) =>
        report.schedule_date === nextValue.schedule_date &&
        nextValue.helper_of === report.assigned_to,
    );
    if (assignedToRole?.role === undefined) {
      return acc + 0; // If assigned role is not found, return 0
    } else {
      // If the assigned role has role_hours, use it to calculate hours
      if (assignedToRole?.role_hours) {
        return acc + Number(assignedToRole.role_hours);
      }
      // If the assigned role is found, use it to calculate base hours
      const assignedRoleData =
        daRoles[daScheduleRoleIdToKey[assignedToRole?.role as number] as DaRole];
      if (assignedRoleData) {
        return acc + (assignedRoleData.baseHours || 0);
      }
      return acc + 0; // If assigned role is not found, return 0
    }
  } else {
    let roleHours =
      !roleData?.flexible && roleData?.baseHours
        ? roleData?.baseHours
        : Number(nextValue.role_hours) > 0
          ? Number(nextValue.role_hours)
          : 0;

    return acc + roleHours;
  }
};

export const returnRoleName = (role: number | undefined) => {
  if (role === undefined || role === null) return "Unknown";
  const roleData = daRoles[daScheduleRoleIdToKey[role]];
  return `${roleData?.name || "Unknown"}`;
};

export const returnRoleHours = (
  role: number | undefined,
  role_hours: number | undefined | null,
  station: number,
  helperOfReport?: ScheduleReport | null | undefined,
) => {
  if (role === undefined || role === null) return "Unknown";
  const roleData = daRoles[daScheduleRoleIdToKey[role]];
  let roleHours =
    roleData.roleScheduleId === 0 && station === 19
      ? 9
      : !roleData?.flexible && roleData?.baseHours
        ? roleData?.baseHours
        : Number(role_hours) > 0
          ? role_hours
          : 0;

  if (role === daScheduleRoleKeyToId.helper && !role_hours && helperOfReport?.role) {
    // If the role is helper and role_hours is not set, calculate based on the helper's assigned report
    if (helperOfReport?.role_hours) {
      // If the helperOfReport has role_hours, use it to calculate hours
      roleHours = Number(helperOfReport.role_hours);
    } else {
      // If the helperOfReport does not have role_hours, use the assigned role's base hours
      const assignedRoleData =
        daRoles[daScheduleRoleIdToKey[helperOfReport?.role as number] as DaRole];
      if (assignedRoleData) {
        roleHours = assignedRoleData.baseHours || 0;
      } else {
        roleHours = 0; // If assigned role is not found, return 0
      }
    }
  }

  return `${roleHours}h`;
};

export const sumCalendarScheduleHours = (acc: number, nextValue: any, dataList: CalendarData[]) => {
  const roleData = daRoles[daScheduleRoleIdToKey[nextValue.role as number] as DaRole];
  if (!roleData) {
    return acc + 0; // If roleData is not found, return 0
  } else if (nextValue.role === daScheduleRoleKeyToId.helper && !nextValue.role_hours) {
    // If the role is helper and role_hours is not set, calculate based on the helper's assigned report
    const assignedToRole = dataList.find(
      (report) => report.schedule_date === nextValue.schedule_date,
    );
    if (assignedToRole?.role === undefined) {
      return acc + 0; // If assigned role is not found, return 0
    } else {
      // If the assigned role has role_hours, use it to calculate hours
      if (assignedToRole?.role_hours) {
        return acc + Number(assignedToRole.role_hours);
      }
      // If the assigned role is found, use it to calculate base hours
      const assignedRoleData =
        daRoles[daScheduleRoleIdToKey[assignedToRole?.role as number] as DaRole];
      if (assignedRoleData) {
        return acc + (assignedRoleData.baseHours || 0);
      }
      return acc + 0; // If assigned role is not found, return 0
    }
  } else {
    let roleHours =
      !roleData?.flexible && roleData?.baseHours
        ? roleData?.baseHours
        : Number(nextValue.role_hours) > 0
          ? Number(nextValue.role_hours)
          : 0;

    return acc + roleHours;
  }
};

export const getCheckDayDate = (currentData: ScheduleDate[] | null, dayName: string) => {
  const dt = currentData?.find((item: ScheduleDate) => item?.day === dayName)?.date;
  if (dt) {
    return `, ${moment(dt).utc(true).format("DD.MM")}`;
  }
};

export const getScheduleColumnDate = (
  dateList: ScheduleDate[] | undefined,
  columnLabel: string,
) => {
  if (!dateList?.length || !columnLabel) return undefined;

  const isSecondWeek = columnLabel.endsWith(" 2");
  const dayName = columnLabel.replace(/ 2$/, "").trim();

  const exactMatch = dateList.find((item) => item.day?.trim() === columnLabel.trim());
  if (exactMatch?.date) return exactMatch.date;

  const matches = dateList.filter((item) => item.day?.replace(/ 2$/, "").trim() === dayName);
  const match = matches[isSecondWeek ? 1 : 0];
  if (match?.date) return match.date;

  if (isSecondWeek && matches[0]?.date) {
    return moment(matches[0].date).add(7, "days").format("YYYY-MM-DD");
  }

  return undefined;
};

export const getScheduleReportForUserDate = (
  dataList: ScheduleReport[] | undefined,
  userId: number,
  date?: string,
) => {
  if (!date || !dataList?.length) return undefined;

  const matches = returnFilteredData(date, userId, dataList);
  const activeReport = matches.find(
    (report) =>
      report.role !== daScheduleRoleKeyToId.mycuCancelled &&
      report.role !== daScheduleRoleKeyToId.clientCancelled,
  );

  return activeReport ?? matches[0];
};

const CONSECUTIVE_ROLE_LIMIT = 7;

const isConsecutiveStreakRole = (report?: ScheduleReport) => {
  if (!report?.is_available || report.role === null) return false;
  return ![
    daScheduleRoleKeyToId.clientCancelled,
    daScheduleRoleKeyToId.mycuCancelled,
    daScheduleRoleKeyToId.rejected,
    daScheduleRoleKeyToId.absent,
  ].includes(report.role);
};

export const isConsecutiveSameRoleLimitReached = (
  dataList: ScheduleReport[] | undefined,
  userId: number,
  date?: string,
) => {
  if (!date || !dataList?.length) return false;

  const current = getScheduleReportForUserDate(dataList, userId, date);
  if (!isConsecutiveStreakRole(current)) return false;

  const role = current?.role;
  let count = 0;
  const cursor = moment(date);

  while (count < CONSECUTIVE_ROLE_LIMIT) {
    const dayDate = cursor.format("YYYY-MM-DD");
    const report =
      dayDate === date ? current : getScheduleReportForUserDate(dataList, userId, dayDate);
    if (!isConsecutiveStreakRole(report) || report?.role !== role) break;
    count += 1;
    cursor.subtract(1, "day");
  }

  return count >= CONSECUTIVE_ROLE_LIMIT;
};

export const getUserScheduleHours = (
  dataList: ScheduleReport[] | undefined,
  userId: number,
  station?: number,
) => {
  if (!dataList?.length) return 0;

  const stationId = station != null ? Number(station) : null;

  return dataList.reduce((sum, report) => {
    if (
      report.assigned_to !== userId ||
      (stationId != null && report.station !== stationId) ||
      !report.is_available ||
      report.role === null ||
      [
        daScheduleRoleKeyToId.clientCancelled,
        daScheduleRoleKeyToId.mycuCancelled,
        daScheduleRoleKeyToId.rejected,
        daScheduleRoleKeyToId.absent,
      ].includes(report.role)
    ) {
      return sum;
    }

    return sum + (Number(report.role_hours) || 0);
  }, 0);
};

export const getScheduleFooterValue = (
  columnId: string,
  station: number,
  date: string | undefined,
  users: UserData[],
  dataList: ScheduleReport[] = [],
) => {
  switch (columnId) {
    case "user":
      return users.length;
    case "hours":
      return `${users.reduce((sum, user) => sum + getUserScheduleHours(dataList, user.id, station), 0)}h`;
    case "tenured":
      return 0;
    default: {
      if (!date) return "";
      const userIds = users.map((user) => user.id);
      const total = dataList
        .filter((report) => report.station === Number(station))
        .filter((report) => report.is_available)
        .filter((report) => report.role !== null)
        .filter(
          (report) =>
            ![
              daScheduleRoleKeyToId.clientCancelled,
              daScheduleRoleKeyToId.mycuCancelled,
              daScheduleRoleKeyToId.rejected,
              daScheduleRoleKeyToId.absent,
            ].includes(report.role),
        )
        .reduce((sum, report) => {
          if (report.schedule_date !== date || report.assigned_to == null) return sum;
          if (!userIds.includes(report.assigned_to)) return sum;
          return sum + (Number(report.role_hours) || 0);
        }, 0);
      return `${total}h`;
    }
  }
};

export const getScheduleDateHeadCounts = (
  date: string | undefined,
  station: number,
  dataList: ScheduleReport[] = [],
  planList: SchedulePlan[] = [],
) => {
  if (!date) return { drivers: 0, helpers: 0 };

  const columnReports = dataList.filter((report) => {
    if (report.schedule_date !== date) return false;
    if (report.station !== Number(station)) return false;
    return true;
  });

  const driverPlan = planList.find(
    (plan) =>
      plan.report_date === date && plan.station === Number(station) && plan.type === "large-van",
  )?.number_of_routes;
  const helperPlan = planList.find(
    (plan) =>
      plan.report_date === date && plan.station === Number(station) && plan.type === "box-truck",
  )?.number_of_routes;

  return {
    drivers: columnReports.filter((report) =>
      [daScheduleRoleKeyToId.driver, daScheduleRoleKeyToId.flex].includes(report.role),
    ).length,
    helpers: columnReports.filter((report) => report.role === daScheduleRoleKeyToId.helper).length,
    driverPlan,
    helperPlan,
  };
};

export const getFilteredScheduleIds = (
  rows: UserData[],
  userRoles: number[] = [],
  shiftRoles: number[] = [],
  search = "",
  sortId?: string,
  dataList: ScheduleReport[] = [],
  station?: number,
) => {
  const query = search?.trim().toLowerCase();
  const userRoleFilter = userRoles?.length ? userRoles : null;
  const shiftRoleFilter = shiftRoles?.length ? shiftRoles : null;
  const usersWithShiftRoles = shiftRoleFilter
    ? dataList.reduce<number[]>((userIds, report) => {
        if (
          report.assigned_to != null &&
          shiftRoleFilter.includes(report.role) &&
          !userIds.includes(report.assigned_to)
        ) {
          userIds.push(report.assigned_to);
        }
        return userIds;
      }, [])
    : null;

  const filtered = rows.filter((row) => {
    if (userRoleFilter && !userRoleFilter.includes(row.role)) return false;
    if (usersWithShiftRoles && !usersWithShiftRoles.includes(row.id)) return false;
    if (!query) return true;

    const firstName = row?.first_name?.toLowerCase() || "";
    const lastName = row?.last_name?.toLowerCase() || "";
    const fullName = `${firstName} ${lastName}`.trim();
    const badge = `${row?.badge}`;
    const tenured = `${row?.tenured}`;

    return (
      fullName.includes(query) ||
      firstName.includes(query) ||
      lastName.includes(query) ||
      badge.includes(query) ||
      tenured.includes(query)
    );
  });

  const columnId = sortId?.replace("-", "") || "badge";
  const isDesc = !!sortId?.startsWith("-");

  return [...filtered]
    .sort((a, b) => compareScheduleSortValues(a, b, columnId, isDesc, dataList, station))
    .map((row) => row.id);
};

const compareScheduleSortValues = (
  a: UserData,
  b: UserData,
  columnId: string,
  isDesc: boolean,
  dataList: ScheduleReport[],
  station?: number,
) => {
  const aValue = getScheduleSortValue(a, columnId, dataList, station);
  const bValue = getScheduleSortValue(b, columnId, dataList, station);

  if (typeof aValue === "number" && typeof bValue === "number") {
    return isDesc ? bValue - aValue : aValue - bValue;
  }

  const result = String(aValue).localeCompare(String(bValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return isDesc ? -result : result;
};

const getScheduleSortValue = (
  row: UserData,
  columnId: string,
  dataList: ScheduleReport[],
  station?: number,
): string | number => {
  switch (columnId) {
    case "user":
      return `${row.first_name || ""} ${row.last_name || ""}`.trim();
    case "badge":
      return row?.badge || "";
    case "transporter_id":
      return row.transporter_id || "";
    case "tenured":
      return row.tenured || "";
    case "role":
      return row.role || "";
    case "hours":
      return getUserScheduleHours(dataList, row.id, station);
    default: {
      const value = row[columnId as keyof UserData];
      if (value == null || typeof value === "object") return "";
      return value as string | number | boolean as string | number;
    }
  }
};
